<template>
  <div class="item-table-container">
    <b-table
      :items="itemsWithChilds"
      :fields="fields"
      responsive
      hover
      borderless
      sticky-header
      class="item-table"
    >
      <template v-slot:cell(details)="row">
        <b-icon
          :icon="row.detailsShowing ? 'chevron-up' : 'chevron-down'"
          @click="row.toggleDetails"
        >
        </b-icon>
      </template>
      <template v-slot:head(details)>
        <div></div>
      </template>
      <template v-slot:head(checkbox)>
        <input type="checkbox" :checked="selectAll" @change="handleSelectAll" />
      </template>
      <template v-slot:cell(checkbox)="{ item }">
        <input
          type="checkbox"
          :checked="selectedRows.includes(item)"
          @change="handleCheckboxChange(item)"
        />
      </template>
      <template v-slot:cell(imgThumbnail)="{ item }">
        <div class="text-center">
          <b-img
            v-if="item.imgThumbnail"
            class="text-center"
            fluid
            :src="item.imgThumbnail + '?width=50&height=50'"
          />
        </div>
      </template>
      <template v-slot:cell(MaterialNumber)="{ item }">
        <div>
          {{ item.MaterialNumber }}
          <button v-if="displaySpecSheet && item.InquiryItemId === 1" class="btn btn-sm btn-outline-primary ml-3" @click="openSpecSheet(item.ItemNumber)"><b-icon icon="file-earmark-text"></b-icon> View Spec Sheet</button>
        </div>
      </template>
      <template v-slot:cell(quantity)="{ item }">
        <input
          type="number"
          :value="item.Quantity"
          class="form-control text-center quantity-input"
          @change="handleQtyChange($event, item)"
          min="1"
          :disabled="disableInput"
        />
      </template>
      <template v-slot:cell(delete)="{ item }">
        <button class="btn-flat btn-red" @click="handleDelete(item)">
          Delete
        </button>
      </template>
      <template #row-details="row">
        <b-card class="small">
          <b-row class="mb-2" v-if="row.item.imgThumbnail">
            <b-col cols="12">
              <div class="text-center">
                <b-img
                  v-if="row.item.imgThumbnail"
                  class="text-center"
                  fluid
                  :src="row.item.imgThumbnail + '?width=100&height=100'"
                />
              </div>
            </b-col>
          </b-row>
          <b-row class="mb-2" v-if="row.item.MaterialNumber">
            <b-col cols="4" class="row-details-label">PCN:</b-col>
            <b-col cols="8"
              ><div class="text-left">
                {{ row.item.MaterialNumber }}
              </div></b-col
            >
          </b-row>
          <b-row class="mb-2" v-if="row.item.fingoodTpiMfgMod">
            <b-col cols="4" class="row-details-label">Model #</b-col>
            <b-col cols="8 text-left">
              {{ row.item.fingoodTpiMfgMod }}
            </b-col>
          </b-row>
          <b-row class="mb-2" v-if="row.item.description">
            <b-col cols="4" class="row-details-label">Description</b-col>
            <b-col cols="8 text-left">
              {{ row.item.description }}
            </b-col>
          </b-row>
          <b-row class="mb-2" v-if="row.item.status">
            <b-col cols="4" class="row-details-label">Status</b-col>
            <b-col cols="8 text-left">
              {{ row.item.status }}
            </b-col>
          </b-row>
          <b-row class="mb-2" v-if="row.item.price">
            <b-col cols="4" class="row-details-label">Unit Price</b-col>
            <b-col cols="8 text-left">
              {{ row.item.price }}
            </b-col>
          </b-row>
          <b-row class="mb-2" v-if="row.item.quantity">
            <b-col cols="4" class="row-details-label">Quantity</b-col>
            <b-col cols="8 text-left">
              {{ row.item.quantity }}
            </b-col>
          </b-row>
          <b-row class="mb-2" v-if="row.item.totalPrice">
            <b-col cols="4" class="row-details-label">Total Price</b-col>
            <b-col cols="8 text-left">
              {{ row.item.totalPrice }}
            </b-col>
          </b-row>
          <b-button
            size="sm"
            class="mt-2 w-100"
            @click="row.toggleDetails"
            variant="outline"
            ><b-icon icon="chevron-double-up" variant="link"
          /></b-button>
        </b-card>
      </template>
    </b-table>
  </div>
</template>

<script lang="ts">
import { CartItem } from '@/store/cart/types'
import Vue from 'vue'
import { Component, Prop } from 'vue-property-decorator'
import { BIcon, BImg } from 'bootstrap-vue'
import { IInquiryItem } from '@/store/inquiry/types'

@Component({
  components: { BIcon, BImg },
})
export default class ItemTable extends Vue {
  @Prop()
  public fields!: []

  @Prop()
  public items!: IInquiryItem[]

  @Prop()
  public selectedRows!: CartItem[]

  @Prop()
  public selectedItems!: CartItem[]

  @Prop()
  public selectAll!: boolean

  @Prop()
  public disableInput!: boolean

  @Prop()
  public displaySpecSheet!: boolean

  @Prop()
  public handleDelete!: (item: CartItem) => void

  @Prop()
  public handleCheckboxChange!: (item: CartItem) => void

  @Prop()
  public handleSelectAll!: () => void

  @Prop()
  public handleQtyChange!: (event: any, item: CartItem) => void

  public formatPrice(cost: number): string {
    return `$${cost.toFixed(2)}`
  }

  public itemNumberText(number: number) {
    return (number * 10 + '').padStart(6, '0')
  }

  public openSpecSheet(itemNumber: string) {
    this.$emit('openSpecSheet', itemNumber)
  }

  public get itemsWithChilds() {
    let itemNumber = 1
    const items: IInquiryItem[] = []
    for (const itemIndex in this.items) {
      const item = this.items[itemIndex]
      if (item.SubItems && item.SubItems.length > 0) {
        for (const subItemIndex in item.SubItems) {
          const subItem = item.SubItems[subItemIndex]
          const conditionPrices = subItem.Conditions.filter((s: any) => s.VariantCondition === 'MISC_SURCHARGE' || s.VariantCondition === 'NEMA4').reduce((partialSum: any, a: any) => partialSum + a.ConditionAmount, 0)
          let multiplier = subItem.SpecialMultiplier ?? subItem.Multiplier
          if (multiplier <= 0) {
            multiplier = 100 + multiplier
          }
          let total = subItem.TotalPrice / subItem.Quantity
          total += 0
          total += (conditionPrices * multiplier / 100)
          total = total * subItem.Quantity
          const listItem: IInquiryItem = {
            InquiryItemId: subItemIndex === '0' ? 1 : 0,
            MaterialNumber: subItem.MaterialNumber,
            Description: subItem.Description,
            Quantity: subItem.Quantity * item.Quantity,
            QuantityUnit: subItem.QuantityUnit,
            ItemNumber: this.itemNumberText(itemNumber++),
            Price: (total / subItem.Quantity),
            Tax: subItem.Tax,
            TotalPrice: (total * item.Quantity),
          }
          items.push(listItem)
        }
      } else {
        item.ItemNumber = this.itemNumberText(itemNumber++)
        items.push(item)
      }
    }
    return items
  }
}
</script>

<style scoped>
.item-icon {
  font-size: 2rem;
}
.item-img {
  width: 60px;
}

.quantity-input {
  max-width: 80px;
  float: right;
}

.image-soon {
  height: 50px;
  width: 50px;
}
</style>
